import fromUnixTime from 'date-fns/fromUnixTime';
import differenceInDays from 'date-fns/differenceInDays';
import Cookies from 'js-cookie';
import {
  ANALYTICS_IDENTITY,
  ANALYTICS_RESET,
  CHATWOOT_RESET,
  CHATWOOT_SET_USER,
} from '../../helper/scriptHelpers';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { LocalStorage } from 'shared/helpers/localStorage';
import { emitter } from 'shared/helpers/mitt';
import { clearAnalyticsConsentDecision } from 'dashboard/helper/analyticsConsent';

Cookies.defaults = { sameSite: 'Lax' };

export const getLoadingStatus = state => state.fetchAPIloadingStatus;
export const setLoadingStatus = (state, status) => {
  state.fetchAPIloadingStatus = status;
};

export const setUser = user => {
  emitter.emit(CHATWOOT_SET_USER, { user });
  emitter.emit(ANALYTICS_IDENTITY, { user });
};

export const getHeaderExpiry = response =>
  fromUnixTime(response.headers.expiry);

export const setAuthCredentials = response => {
  const expiryDate = getHeaderExpiry(response);
  Cookies.set('cw_d_session_info', JSON.stringify(response.headers), {
    expires: differenceInDays(expiryDate, new Date()),
  });
  setUser(response.data.data, expiryDate);
};

export const clearBrowserSessionCookies = () => {
  Cookies.remove('cw_d_session_info');
  Cookies.remove('auth_data');
  Cookies.remove('user');
};

export const clearLocalStorageOnLogout = () => {
  LocalStorage.remove(LOCAL_STORAGE_KEYS.DRAFT_MESSAGES);
};

export const deleteIndexedDBOnLogout = async () => {
  let dbs = [];
  try {
    dbs = await window.indexedDB.databases();
    dbs = dbs.map(db => db.name);
  } catch (e) {
    dbs = JSON.parse(localStorage.getItem('cw-idb-names') || '[]');
  }

  dbs.forEach(dbName => {
    const deleteRequest = window.indexedDB.deleteDatabase(dbName);

    deleteRequest.onerror = event => {
      // eslint-disable-next-line no-console
      console.error(`Error deleting database ${dbName}.`, event);
    };

    deleteRequest.onsuccess = () => {
      // eslint-disable-next-line no-console
      console.log(`Database ${dbName} deleted successfully.`);
    };
  });

  localStorage.removeItem('cw-idb-names');
};

export const clearCookiesOnLogout = () => {
  emitter.emit(CHATWOOT_RESET);
  emitter.emit(ANALYTICS_RESET);
  clearBrowserSessionCookies();
  // #1825 — a decisao sobre as tags e de uma PESSOA, nao da maquina. Computador compartilhado
  // e rotina no atendimento, e deixar o `granted` de quem saiu valendo para quem entra depois
  // mediria alguem que nunca foi perguntado. Os cookies das ferramentas (`_ga`, `_clck`) NAO
  // sao apagados aqui: quem os apaga e a revogacao, e sair da conta nao e revogar.
  clearAnalyticsConsentDecision();
  clearLocalStorageOnLogout();
  const globalConfig = window.globalConfig || {};
  const logoutRedirectLink = globalConfig.LOGOUT_REDIRECT_LINK || '/';
  window.location = logoutRedirectLink;
};

const formatAttributeName = attributeName =>
  attributeName
    .toString()
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const parseStringPayload = payload => {
  if (typeof payload !== 'string') {
    return payload;
  }

  const trimmedPayload = payload.trim();
  if (!trimmedPayload || trimmedPayload === '[object Object]') {
    return '';
  }

  try {
    return JSON.parse(trimmedPayload);
  } catch {
    return trimmedPayload;
  }
};

const flattenErrorPayload = payload => {
  const parsedPayload = parseStringPayload(payload);

  if (!parsedPayload) {
    return [];
  }

  if (typeof parsedPayload === 'string') {
    return parsedPayload ? [parsedPayload] : [];
  }

  if (parsedPayload instanceof Error) {
    return flattenErrorPayload(parsedPayload.message);
  }

  if (Array.isArray(parsedPayload)) {
    return parsedPayload.flatMap(flattenErrorPayload);
  }

  if (typeof parsedPayload === 'object') {
    if (parsedPayload.message) {
      return flattenErrorPayload(parsedPayload.message);
    }

    if (parsedPayload.error) {
      return flattenErrorPayload(parsedPayload.error);
    }

    if (parsedPayload.errors) {
      return flattenErrorPayload(parsedPayload.errors);
    }

    return Object.entries(parsedPayload).flatMap(([attribute, messages]) => {
      const parsedMessages = flattenErrorPayload(messages);
      const formattedAttribute = formatAttributeName(attribute);

      return parsedMessages.map(message => {
        if (!formattedAttribute) {
          return message;
        }

        const normalizedMessage = message.toLowerCase();
        const normalizedAttribute = formattedAttribute.toLowerCase();

        if (normalizedMessage.startsWith(normalizedAttribute)) {
          return message;
        }

        return `${formattedAttribute} ${message}`;
      });
    });
  }

  return [];
};

export const parseAPIErrorResponse = error => {
  const errorPayload =
    error?.response?.data ?? error?.data ?? error?.message ?? error;

  const errorMessages = flattenErrorPayload(errorPayload).filter(Boolean);
  if (errorMessages.length) {
    return errorMessages.join(', ');
  }

  const parsedMessage = parseStringPayload(error?.message);
  if (typeof parsedMessage === 'string' && parsedMessage) {
    return parsedMessage;
  }

  return '';
};

export const throwErrorMessage = error => {
  const errorMessage = parseAPIErrorResponse(error);
  if (errorMessage) {
    throw new Error(errorMessage);
  }

  throw error;
};
